import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

interface Project {
  id: string;
  name: string;
  total_investment: number;
}

interface JudgeInvestment {
  identifier: string;
  display_name: string;
  role?: 'judge' | 'investor';
  is_voted: boolean;
  investments: Record<string, number>;
  total_investment: number;
  scores: Record<string, number>;
  total_score: number;
}

interface ProjectsResponse {
  projects: Project[];
  total_budget: number;
  remaining_budget: number;
  venue_id?: string;
  venue_name?: string;
  judge_investments?: JudgeInvestment[];
}

interface RankedProject {
  id: string;
  name: string;
  totalScore: number;
  totalInvestment: number;
  rank: number;
}

interface ScoreRankingProps {
  venueId: string;
  isPresentationMode?: boolean;
  onPresentationModeChange?: (mode: boolean) => void;
  isAdmin?: boolean;
  judgeIdentifierAllowlist?: string[];
}

const JUDGE_COLOR_PALETTE = ['#EAB308', '#22C55E', '#F59E0B', '#16A34A', '#D97706', '#15803D'];
const SCORE_RANKING_CACHE_PREFIX = 'fundthepitch_score_ranking_snapshot_';
const SCORE_RANKING_POLL_MS_ACTIVE = 3000;
const SCORE_RANKING_POLL_MS_HIDDEN = 12000;

interface ScoreRankingSnapshotCache {
  savedAt: number;
  signature: string;
  payload: ProjectsResponse;
}

function ScoreRanking({
  venueId,
  isPresentationMode = false,
  onPresentationModeChange,
  isAdmin = false,
  judgeIdentifierAllowlist,
}: ScoreRankingProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [judgeInvestments, setJudgeInvestments] = useState<JudgeInvestment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [venueName, setVenueName] = useState('');

  const [revealedCount, setRevealedCount] = useState(0);
  const [isAutoReveal, setIsAutoReveal] = useState(false);
  const [isRankingListExpanded, setIsRankingListExpanded] = useState(false);

  const podiumAreaRef = useRef<HTMLDivElement>(null);
  const prevRevealedCountRef = useRef(0);
  const lastDataSignatureRef = useRef('');
  const cacheKey = `${SCORE_RANKING_CACHE_PREFIX}${venueId}`;

  const buildResponseSignature = useCallback((payload: ProjectsResponse): string => {
    const projectRows = [...(payload.projects || [])]
      .map((project) => [project.id, Number(project.total_investment || 0), project.name] as const)
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]), 'zh-Hant'));

    const judgeRows = [...(payload.judge_investments || [])]
      .map((judge) => {
        const scoreRows = Object.entries(judge.scores || {})
          .map(([projectId, score]) => [projectId, Number(score || 0)] as const)
          .sort((a, b) => a[0].localeCompare(b[0], 'zh-Hant'));
        return [
          judge.identifier,
          judge.display_name,
          judge.role || 'judge',
          judge.is_voted ? 1 : 0,
          Number(judge.total_score || 0),
          scoreRows,
        ] as const;
      })
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]), 'zh-Hant'));

    return JSON.stringify({
      venue_name: payload.venue_name || '',
      projectRows,
      judgeRows,
    });
  }, []);

  const applyProjectsPayload = useCallback((payload: ProjectsResponse, fromCache = false, savedAt?: number): void => {
    const signature = buildResponseSignature(payload);
    const hasChanged = signature !== lastDataSignatureRef.current;

    setVenueName(payload.venue_name || venueId);

    if (hasChanged) {
      lastDataSignatureRef.current = signature;
      setProjects(payload.projects || []);
      setJudgeInvestments(payload.judge_investments || []);
      setLastUpdated(new Date(savedAt ?? Date.now()));

      if (!fromCache) {
        const snapshot: ScoreRankingSnapshotCache = {
          savedAt: Date.now(),
          signature,
          payload,
        };
        localStorage.setItem(cacheKey, JSON.stringify(snapshot));
      }
    }

    setError(null);
    setLoading(false);
  }, [buildResponseSignature, cacheKey, venueId]);

  const fetchProjects = useCallback(async (): Promise<void> => {
    try {
      const response = await axios.get<ProjectsResponse>(`${API_BASE_URL}/api/projects`, {
        params: { venue_id: venueId },
      });
      applyProjectsPayload(response.data, false);
    } catch (err: unknown) {
      setError('發生錯誤，請聯絡系統管理員');
      console.error('Error fetching score ranking data:', err);
      setLoading(false);
    }
  }, [applyProjectsPayload, venueId]);

  useEffect(() => {
    lastDataSignatureRef.current = '';
    setLoading(true);
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as ScoreRankingSnapshotCache;
      if (!parsed || typeof parsed !== 'object' || !parsed.payload) {
        return;
      }
      applyProjectsPayload(parsed.payload, true, parsed.savedAt);
    } catch {
      // Ignore corrupted cache data and continue with API polling.
    }
  }, [applyProjectsPayload, cacheKey]);

  useEffect(() => {
    let intervalId: number | undefined;

    const runFetch = () => {
      void fetchProjects();
    };

    const schedule = () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
      intervalId = window.setInterval(
        runFetch,
        document.hidden ? SCORE_RANKING_POLL_MS_HIDDEN : SCORE_RANKING_POLL_MS_ACTIVE,
      );
    };

    const onVisibilityChange = () => {
      if (!document.hidden) {
        runFetch();
      }
      schedule();
    };

    runFetch();
    schedule();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [fetchProjects]);

  const scoringJudges = useMemo(() => {
    const allowlist = new Set((judgeIdentifierAllowlist || []).filter(Boolean));
    if (allowlist.size > 0) {
      return judgeInvestments.filter((judge) => allowlist.has(judge.identifier));
    }
    return judgeInvestments.filter((judge) => judge.role !== 'investor');
  }, [judgeIdentifierAllowlist, judgeInvestments]);

  const rankedProjects = useMemo<RankedProject[]>(() => {
    const scored = projects.map((project) => {
      const totalScore = scoringJudges.reduce((sum, judge) => {
        const score = judge.scores?.[project.id] || 0;
        return sum + Number(score);
      }, 0);
      return {
        id: project.id,
        name: project.name,
        totalScore,
        totalInvestment: Number(project.total_investment || 0),
        rank: 0,
      };
    });

    scored.sort((a, b) => {
      if (b.totalScore !== a.totalScore) {
        return b.totalScore - a.totalScore;
      }
      if (b.totalInvestment !== a.totalInvestment) {
        return b.totalInvestment - a.totalInvestment;
      }
      return a.name.localeCompare(b.name, 'zh-Hant');
    });

    return scored.map((item, index) => ({
      ...item,
      rank: index + 1,
    }));
  }, [projects, scoringJudges]);

  const topProjects = useMemo(() => {
    const maxCount = Math.min(5, rankedProjects.length);
    return rankedProjects.slice(0, maxCount);
  }, [rankedProjects]);

  // Reveal from 佳作開始：先揭曉第 5、4 名，再依序揭曉前三名。
  const revealSequence = useMemo(() => {
    return [...topProjects].sort((a, b) => b.rank - a.rank);
  }, [topProjects]);

  useEffect(() => {
    if (!isAutoReveal || revealedCount >= revealSequence.length) {
      return;
    }
    const timer = window.setTimeout(() => {
      setRevealedCount((prev) => prev + 1);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [isAutoReveal, revealSequence.length, revealedCount]);

  useEffect(() => {
    setRevealedCount((prev) => Math.min(prev, revealSequence.length));
  }, [revealSequence.length]);

  useEffect(() => {
    if (isPresentationMode) {
      setRevealedCount(0);
      prevRevealedCountRef.current = 0;
      setIsAutoReveal(false);
      setTimeout(() => {
        if (podiumAreaRef.current) {
          podiumAreaRef.current.scrollTop = podiumAreaRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [isPresentationMode]);

  // Scroll to newly revealed item on single-step reveal
  useEffect(() => {
    if (!isPresentationMode || revealedCount === 0) {
      prevRevealedCountRef.current = revealedCount;
      return;
    }
    const delta = revealedCount - prevRevealedCountRef.current;
    prevRevealedCountRef.current = revealedCount;
    if (delta !== 1) return;
    const revealedProject = revealSequence[revealedCount - 1];
    if (!revealedProject || !podiumAreaRef.current) return;
    setTimeout(() => {
      const el = podiumAreaRef.current?.querySelector(`[data-rank="${revealedProject.rank}"]`);
      if (el) {
        (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  }, [revealedCount, isPresentationMode, revealSequence]);

  const getRankLabel = (rank: number): string => {
    return rank === 4 || rank === 5 ? '佳作' : `第 ${rank} 名`;
  };

  const getJudgeColor = (index: number): string => {
    return JUDGE_COLOR_PALETTE[index % JUDGE_COLOR_PALETTE.length];
  };

  const isJudgeScoreRevealed = (judge: JudgeInvestment, projectId: string): boolean => {
    if (judge.role === 'investor') {
      return false;
    }
    if (!judge.is_voted) {
      return false;
    }
    const score = judge.scores?.[projectId];
    return typeof score === 'number';
  };

  // Helper function to clean display_name by removing "name::" prefix
  const cleanDisplayName = (displayName: string | undefined): string => {
    if (!displayName) return '';
    if (displayName.startsWith('name::')) {
      return displayName.substring(6); // Remove "name::" prefix
    }
    return displayName;
  };

  const getAverageScore = (projectId: string): { average: number | null; count: number } => {
    const scoredValues = scoringJudges
      .filter((judge) => isJudgeScoreRevealed(judge, projectId))
      .map((judge) => {
        const score = judge.scores?.[projectId];
        return typeof score === 'number' ? Number(score) : 0;
      });

    if (scoredValues.length === 0) {
      return { average: null, count: 0 };
    }

    const sum = scoredValues.reduce((total, score) => total + score, 0);
    return {
      average: sum / scoredValues.length,
      count: scoredValues.length,
    };
  };

  if (loading) {
    return (
      <div className="dashboard container">
        <section className="section">
          <h2>最終評分排名</h2>
          <p>正在同步 {venueName || venueId} 的評分資料...</p>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard container">
        <section className="section">
          <h2>最終評分排名</h2>
          <div className="error-toast">{error}</div>
          <p>系統正在嘗試重新連接...</p>
        </section>
      </div>
    );
  }

  // Podium helpers
  const revealedSet = new Set(revealSequence.slice(0, revealedCount).map((item) => item.rank));
  const podiumSlots = [
    { rank: 2, icon: '🥈', colorClass: 'rank-2' },
    { rank: 1, icon: '🥇', colorClass: 'rank-1' },
    { rank: 3, icon: '🥉', colorClass: 'rank-3' },
  ];
  const belowPodiumProjects = topProjects
    .filter((p) => p.rank > 3)
    .sort((a, b) => a.rank - b.rank);

  return (
    <>
      {isPresentationMode && isAdmin && (
        <div className="presentation-modal-backdrop" role="dialog" aria-modal="true">
          <div className="presentation-modal score-ranking-modal">
            <div className="presentation-header">
              <h1>{venueName || venueId} - 最終評分排名</h1>
              <button
                className="presentation-exit-btn"
                onClick={() => onPresentationModeChange?.(false)}
                title="關閉最終評分排名"
              >
                ✕ 關閉最終排名
              </button>
            </div>

            <div className="ranking-controls">
              <button
                className="ranking-btn"
                onClick={() => setRevealedCount(revealSequence.length)}
              >
                📊 一次公布所有結果
              </button>
              <button
                className={`ranking-btn ${isAutoReveal ? 'active' : ''}`}
                onClick={() => setIsAutoReveal((prev) => !prev)}
              >
                {isAutoReveal ? '⏸ 暫停' : '▶ 逐個揭曉排名'}
              </button>
              {revealedCount < revealSequence.length && !isAutoReveal && (
                <button
                  className="ranking-btn next-btn"
                  onClick={() => setRevealedCount((prev) => Math.min(prev + 1, revealSequence.length))}
                >
                  ⏭ 下一個
                </button>
              )}
            </div>

            <div className="podium-area" ref={podiumAreaRef}>
              <div className="podium-stage">
                {podiumSlots.map(({ rank, icon, colorClass }) => {
                const project = topProjects.find((p) => p.rank === rank);
                  const isRevealed = !!project && revealedSet.has(rank);
                  return (
                    <div
                      key={rank}
                      className={`podium-column ${colorClass}${isRevealed ? ' revealed' : ''}${!project ? ' empty' : ''}`}
                      data-rank={rank}
                    >
                      <div className="podium-info">
                        {isRevealed && project ? (
                          <>
                            <div className="podium-project-name">{project.name}</div>
                            <div className="podium-project-amount" />
                          </>
                        ) : (
                          <>
                            <div className="podium-project-name podium-placeholder">？？？</div>
                            <div className="podium-project-amount podium-placeholder">—</div>
                          </>
                        )}
                      </div>
                      <div className={`podium-block ${colorClass}`}>
                        <span className="podium-rank-label">{icon} 第 {rank} 名</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {belowPodiumProjects.length > 0 && (
                <div className="below-podium-zone">
                  {belowPodiumProjects.map((project) => {
                    const isRevealed = revealedSet.has(project.rank);
                    return (
                      <div
                        key={project.id}
                        className={`below-podium-item${isRevealed ? ' revealed' : ''}`}
                        data-rank={project.rank}
                      >
                        <span className="bp-rank">{getRankLabel(project.rank)}</span>
                        <span className="bp-name">
                          {isRevealed ? project.name : '？？？'}
                        </span>
                        <span className="bp-amount">{isRevealed ? '' : '—'}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="presentation-footer">
              <div className="footer-stat">
                <span className="footer-label">公開揭曉專題數</span>
                <span className="footer-value">{topProjects.length}</span>
              </div>
              <div className="footer-stat">
                <span className="footer-label">揭曉進度</span>
                <span className="footer-value">{revealedCount} / {revealSequence.length}</span>
              </div>
              <div className="footer-stat">
                <span className="footer-label">最後更新</span>
                <span className="footer-value">{lastUpdated.toLocaleTimeString('zh-Hant-TW')}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard container">
        <section className="section dashboard-header">
          <h2>{venueName || venueId} - 最終評分排名</h2>
          <p>依各組最終評分資料即時更新排名結果。</p>
          <div className="dashboard-presentation-actions">
            {isAdmin && (
              <button
                type="button"
                className="submit-button"
                onClick={() => onPresentationModeChange?.(true)}
              >
                顯示最終排名
              </button>
            )}
          </div>
        </section>

        <section className="section dashboard-stats">
          <div className="stat-card">
            <h3>評審人數</h3>
            <div className="stat-value">{scoringJudges.length}</div>
          </div>
          <div className="stat-card">
            <h3>最後更新</h3>
            <div className="stat-value">{lastUpdated.toLocaleTimeString('zh-Hant-TW')}</div>
          </div>
        </section>

        <section className="section dashboard-details">
          <div className="details-header">
            <h3>最終排名清單</h3>
            <button
              type="button"
              className="toggle-button"
              onClick={() => setIsRankingListExpanded((prev) => !prev)}
            >
              {isRankingListExpanded ? '收合排名' : '展開排名'}
            </button>
          </div>
          {!isRankingListExpanded ? (
            <div className="collapsed-note">
              最終排名資料已隱藏，點選「展開排名」查看評分細節。
            </div>
          ) : (
            <table className="details-table">
              <thead>
                <tr>
                  <th>排名</th>
                  <th>專題名稱</th>
                  <th>評審評分</th>
                  <th>平均評分</th>
                </tr>
              </thead>
              <tbody>
              {rankedProjects.map((item) => {
                const avgScore = getAverageScore(item.id);
                return (
                  <tr key={item.id}>
                    <td>{getRankLabel(item.rank)}</td>
                    <td>{item.name}</td>
                    <td>
                      <div className="judge-allocation-list">
                        {scoringJudges.length === 0 && (
                          <span className="judge-allocation-empty">尚無評審評分資料</span>
                        )}
                        {scoringJudges.length > 0 && scoringJudges.map((judge, judgeIndex) => {
                          const isRevealed = isJudgeScoreRevealed(judge, item.id);
                          const scoreText = isRevealed
                            ? `${Number(judge.scores[item.id]).toLocaleString()} 分`
                            : '未評分';
                          const cleanedName = cleanDisplayName(judge.display_name);
                          return (
                            <span key={`${item.id}-${judge.identifier}`} className="judge-allocation-item">
                              <span
                                className="judge-allocation-dot"
                                style={{ backgroundColor: getJudgeColor(judgeIndex) }}
                              />
                              {cleanedName}: {scoreText}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      {avgScore.average === null
                        ? '未評分'
                        : `${avgScore.average.toFixed(1)} 分 (${avgScore.count} 人)`}
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}

export default ScoreRanking;
